import { Box, Button, Container, Header, SpaceBetween, StatusIndicator, Table } from '@cloudscape-design/components';
import { FC } from 'react';
import Flex from '../../components/Flex';
import { useAuthContext } from '../../contexts/AuthContext';
import { useModuleTranslation } from '../../lang';
import { Batch } from '../../models/Batch';
import { Movement } from '../../pages/AgpOverview.logic';
import { AgpTableBaseProps } from './types';

type Props = AgpTableBaseProps & {
  releasesWithTotal: Array<Movement & { __isTotal?: boolean }>;
  totalExcise: number;
  batchMap: Record<string, Batch>;
  computeExciseEUR: (m: { size: number; amount: number; batchId: string }) => number;
};

const ReleasesTable: FC<Props> = ({
  releasesWithTotal,
  totalExcise,
  batchMap,
  computeExciseEUR,
  headerActions,
  refresh,
  loading,
  loadingText,
  emptyText,
  testId,
}) => {
  const { currency } = useAuthContext();
  const t = useModuleTranslation('Agp.ReleasesTable');
  return (
    <Container data-testid={testId}>
      <Header
        variant="h2"
        actions={
          <SpaceBetween size="xs" direction="horizontal">
            {headerActions}
            {refresh && <Button key="refresh" iconName="refresh" variant="icon" onClick={refresh} disabled={loading} />}
          </SpaceBetween>
        }
      >
        {t.header}
      </Header>

      <Table<Movement & { __isTotal?: boolean }>
        trackBy={(m) => m.id}
        variant="borderless"
        stickyHeader
        loading={loading}
        loadingText={loadingText}
        columnDefinitions={[
          { header: t.date, cell: (m) => (m.__isTotal ? '' : m.date.toLocaleString()) },
          { header: t.product, cell: (m) => (m.__isTotal ? '' : m.slug.charAt(0).toUpperCase() + m.slug.slice(1)) },
          { header: t.bottleSize, cell: (m) => (m.__isTotal ? '' : m.bottleName) },
          { header: t.amount, cell: (m) => (m.__isTotal ? '' : m.amount) },
          {
            header: t.batch,
            cell: (m) =>
              m.__isTotal ? (
                ''
              ) : (
                <Flex>
                  {batchMap[m.batchId]?.batchId ?? ''}
                  <Box variant="awsui-gen-ai-label">{m.batchId}</Box>
                </Flex>
              ),
          },
          { header: t.note, cell: (m) => (m.__isTotal ? '' : (m.note ?? '')) },
          {
            header: t.excise,
            cell: (m) =>
              m.__isTotal ? (
                <b>{currency(totalExcise.toFixed(2))}</b>
              ) : (
                ` ${currency(computeExciseEUR({ size: m.size, amount: m.amount, batchId: m.batchId }).toFixed(2))}`
              ),
          },
          {
            header: t.exciseStatus,
            cell: (m) =>
              !m.__isTotal ? (
                m.excisePaymentReferenceId ? (
                  <StatusIndicator type="success">{t.paid}</StatusIndicator>
                ) : (
                  <StatusIndicator type="pending">{t.unpaid}</StatusIndicator>
                )
              ) : (
                ''
              ),
            minWidth: 140,
          },
          {
            header: t.paidOn,
            cell: (m) => (!m.__isTotal ? (m.excisePaidDate ? m.excisePaidDate.toLocaleDateString() : '—') : ''),
            minWidth: 120,
          },
          {
            header: t.paymentRef,
            cell: (m) => (!m.__isTotal ? (m.excisePaymentReferenceId ?? '—') : ''),
            minWidth: 180,
          },
        ]}
        items={releasesWithTotal.filter((m) => !m.sold)}
        empty={<Box padding="m">{emptyText ?? t.empty}</Box>}
      />
    </Container>
  );
};

export default ReleasesTable;
